'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { parseHttpRequest, parseHttpResponse } from '@terra/shared-utils';
import { toasterApi } from '../../toast/ToastProvider';

/**
 * Data structure for HTTP request/response comparison
 */
export type HttpComparerData = {
  method: string;
  url: string;
  headers?: Record<string, string | string[]>;
  body?: string;
  httpVersion?: string;
  /** Optional label for the comparer item */
  label?: string;
  /** Whether this is a request or response */
  type?: 'request' | 'response';
  /** Optional status code for responses */
  status?: number;
  /** Response headers for responses */
  responseHeaders?: Record<string, string | string[]>;
  /** Response body for responses */
  responseBody?: string;
};

type HttpComparerContextType = {
  /**
   * Send an HTTP request/response to the comparer tab for comparison.
   */
  sendToComparer: (data: HttpComparerData) => void;
  /**
   * Send raw HTTP content (in raw HTTP format) to the comparer tab.
   * The content will be parsed to extract method, url, headers, and body.
   */
  sendRawToComparer: (content: string, type?: 'request' | 'response') => void;
  /** Whether the comparer feature is available */
  isAvailable: boolean;
};

const HttpComparerContext = createContext<HttpComparerContextType | undefined>(
  undefined,
);

type HttpComparerProviderProps = {
  children: ReactNode;
  /**
   * Callback to handle sending HTTP request/response data to the comparer tab.
   * If not provided, the comparer feature will be marked as unavailable.
   */
  onSendToComparer?: (data: HttpComparerData) => void;
};

function parseRawHttpContent(
  content: string,
  type: 'request' | 'response' = 'request',
): HttpComparerData | null {
  if (type === 'response' || content.trimStart().startsWith('HTTP/')) {
    const parsed = parseHttpResponse(content);
    if (!parsed) {
      return null;
    }

    return {
      method: 'GET',
      url: '',
      httpVersion: parsed.httpVersion,
      status: parsed.statusCode,
      responseHeaders:
        Object.keys(parsed.headers).length > 0 ? parsed.headers : undefined,
      responseBody: parsed.body.trim() ? parsed.body : undefined,
      type: 'response',
    };
  }

  const parsed = parseHttpRequest(content);
  if (!parsed) {
    return null;
  }

  return {
    method: parsed.method,
    url: parsed.url,
    httpVersion: parsed.httpVersion,
    headers:
      Object.keys(parsed.headers).length > 0 ? parsed.headers : undefined,
    body: parsed.body.trim() ? parsed.body : undefined,
    type: 'request',
  };
}

export function HttpComparerProvider({
  children,
  onSendToComparer,
}: HttpComparerProviderProps) {
  const contextValue: HttpComparerContextType = useMemo(
    () => ({
      sendToComparer: (data: HttpComparerData) => {
        if (onSendToComparer) {
          onSendToComparer(data);
        }
      },
      sendRawToComparer: (
        content: string,
        type: 'request' | 'response' = 'request',
      ) => {
        if (onSendToComparer) {
          const parsed = parseRawHttpContent(content, type);
          if (parsed) {
            onSendToComparer(parsed);
            return;
          }
          toasterApi.error({
            title: 'Failed to send to comparer',
            description: 'Could not parse the HTTP content. Check the format.',
          });
        }
      },
      isAvailable: !!onSendToComparer,
    }),
    [onSendToComparer],
  );

  return (
    <HttpComparerContext.Provider value={contextValue}>
      {children}
    </HttpComparerContext.Provider>
  );
}

/**
 * Hook to access the HTTP comparer context.
 * Returns undefined if not within a provider.
 */
export function useHttpComparerContext(): HttpComparerContextType | undefined {
  return useContext(HttpComparerContext);
}

/**
 * Hook to get the sendRawToComparer function if available.
 * Returns undefined if not within a provider or comparer is not available.
 */
export function useSendToComparer():
  | ((content: string, type?: 'request' | 'response') => void)
  | undefined {
  const context = useContext(HttpComparerContext);
  if (!context?.isAvailable) return undefined;
  return context.sendRawToComparer;
}
